import { useEffect, useState, type AnimationEvent, type CSSProperties } from "react";
import type { PlacedThought, ThoughtAnchor } from "../types/adventure";

interface ThoughtCloudProps {
  thought: PlacedThought;
  systemLabel: string;
  systemIcon: string;
  selectable?: boolean;
  selected?: boolean;
  faded?: boolean;
  pinned?: boolean;
  pinSlot?: number;
  onToggleSelect?: (thoughtId: string) => void;
  /** Fires once when the spawn glide finishes (or immediately with reduced motion). */
  onSettled?: () => void;
}

function anchorTranslateX(anchor: ThoughtAnchor): string {
  if (anchor === "left") return "-100%";
  if (anchor === "center") return "-50%";
  return "0%";
}

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function ThoughtContent({
  systemIcon,
  systemLabel,
  text,
}: {
  systemIcon: string;
  systemLabel: string;
  text: string;
}) {
  return (
    <>
      <span className="act-thought__source" aria-hidden>
        {systemIcon} {systemLabel}
      </span>
      <span className="act-thought__text">{text}</span>
    </>
  );
}

export function ThoughtCloud({
  thought,
  systemLabel,
  systemIcon,
  selectable = false,
  selected = false,
  faded = false,
  pinned = false,
  pinSlot = 0,
  onToggleSelect,
  onSettled,
}: ThoughtCloudProps) {
  const shouldEnter = !pinned && !faded;
  const [entered, setEntered] = useState(() => !shouldEnter || prefersReducedMotion());
  const spawning = shouldEnter && !entered;

  useEffect(() => {
    if (!onSettled || !shouldEnter) return;
    if (prefersReducedMotion()) {
      onSettled();
    }
  }, [onSettled, shouldEnter]);

  const className = [
    "act-thought",
    spawning ? "act-thought--spawning" : "",
    thought.overlay ? "act-thought--overlay" : "",
    !pinned && entered && thought.anchor === "left" ? "act-thought--anchor-left" : "",
    !pinned && entered && thought.anchor === "right" ? "act-thought--anchor-right" : "",
    !pinned && entered && thought.anchor === "center" ? "act-thought--anchor-center" : "",
    selectable ? "act-thought--selectable" : "",
    selected ? "act-thought--selected" : "",
    faded ? "act-thought--faded" : "",
    pinned ? "act-thought--pinned" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const { float } = thought;
  const outerStyle = {
    ...(pinned
      ? {}
      : spawning
        ? {
            "--target-x": `${thought.x}%`,
            "--target-y": `${thought.y}%`,
            "--anchor-tx": anchorTranslateX(thought.anchor),
            "--spawn-x": `${float.spawnX}px`,
            "--spawn-y": `${float.spawnY}px`,
          }
        : {
            left: `${thought.x}%`,
            top: `${thought.y}%`,
          }),
    "--pin-slot": pinSlot,
  } as CSSProperties;

  const floatStyle = {
    "--float-x1": `${float.x1}px`,
    "--float-y1": `${float.y1}px`,
    "--float-x2": `${float.x2}px`,
    "--float-y2": `${float.y2}px`,
    "--float-x3": `${float.x3}px`,
    "--float-y3": `${float.y3}px`,
    "--float-duration": `${float.duration}s`,
    "--float-delay": `${float.delay}s`,
  } as CSSProperties;

  const handleAnimationEnd = (event: AnimationEvent<HTMLElement>) => {
    if (event.animationName === "act-thought-spawn-glide") {
      setEntered(true);
      onSettled?.();
    }
  };

  const inner = (
    <span
      className={spawning ? "act-thought__float act-thought__float--spawning" : "act-thought__float"}
      style={floatStyle}
    >
      <ThoughtContent
        systemIcon={systemIcon}
        systemLabel={systemLabel}
        text={thought.text}
      />
    </span>
  );

  if (selectable && onToggleSelect) {
    return (
      <button
        type="button"
        className={className}
        style={outerStyle}
        onAnimationEnd={handleAnimationEnd}
        onClick={() => onToggleSelect(thought.thoughtId)}
        aria-label={`${selected ? "Deselecteer" : "Selecteer"} helpende gedachte: ${thought.text}`}
        aria-pressed={selected}
      >
        {inner}
      </button>
    );
  }

  return (
    <div
      className={className}
      style={outerStyle}
      onAnimationEnd={handleAnimationEnd}
      aria-live="polite"
    >
      {inner}
    </div>
  );
}
